import { Sparkles, Tag, type LucideIcon } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/core/card';

const SECTION_CARD_CLASS =
  'rounded-xl border border-rose-200 border-b-[4px] border-b-rose-300 bg-gradient-to-b from-white to-gray-50/60 shadow-[inset_0_1px_0_rgba(255,255,255,0.8),0_4px_0_rgba(0,0,0,0.05),0_6px_12px_rgba(0,0,0,0.04)]';

type Tone = 'skill' | 'theme';

const TONES: Record<
  Tone,
  { Icon: LucideIcon; title: string; chip: string }
> = {
  // Skills share the calm slate chip with themes; the title icon keeps them distinct.
  skill: {
    Icon: Sparkles,
    title: 'text-rose-500',
    chip: 'border-slate-200 border-b-slate-300 bg-gradient-to-b from-slate-50 to-slate-100/70 text-slate-700',
  },
  // Themes use a calm slate so the two sections are instantly distinguishable.
  theme: {
    Icon: Tag,
    title: 'text-slate-400',
    chip: 'border-slate-200 border-b-slate-300 bg-gradient-to-b from-slate-50 to-slate-100/70 text-slate-700',
  },
};

const CHIP_BASE =
  'inline-flex items-center rounded-full border border-b-2 px-3 py-1 text-[13px] font-medium leading-snug shadow-[inset_0_1px_0_rgba(255,255,255,0.7)] transition-transform duration-100 hover:-translate-y-px';

export function TagCard({ title, items, tone }: { title: string; items: string[]; tone: Tone }) {
  const { Icon, title: titleIconClass, chip } = TONES[tone];

  return (
    <Card className={SECTION_CARD_CLASS}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base font-semibold text-gray-800">
          <Icon className={`h-4 w-4 ${titleIconClass}`} aria-hidden="true" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <p className="text-sm text-gray-400">&mdash;</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {items.map((item, i) => (
              <span key={i} className={`${CHIP_BASE} ${chip}`}>
                {item}
              </span>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
